import React from 'react';

import {Box} from '@material-ui/core';
import {useHistory} from 'react-router-dom';
import {useSnackbar} from 'notistack';

import {
    View,
    Field,
    EfeResponse,
    EfeAccordionValues,
    BeneficiaryServiceRequest,
} from 'commons/models';
import {
    Messages,
    ViewKeys,
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    SnackbarVariants,
} from 'commons/constants';
import {ClientService} from 'services';
import {EfeAccordion} from 'components';
import {useAlert, useSpinner} from 'hooks';
import {MainContent} from 'layouts/commons';
import {BeneficiaryInfo} from 'commons/types/Types';
import PersonalInfoForm from './form/PersonalInfoForm';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {fieldCatalogGetter, useFlowFieldSearch} from 'hooks/FlowHook';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {clearUnusedFields, formatDateToISO, nextStep} from 'commons/utils/Functions';
import {useBeneficiaryContext} from 'context/beneficiary-context/BeneficiaryContext';

function BeneficiaryPersonalInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {menu, expedientId, fatherKycId, setKycId, kycId} = useMenuContext();
    const {
        percentageCompleted,
        setPercentageCompleted,
        beneficiaries,
        setBeneficiaries,
        setTempBeneficiary,
        setActive,
    } = useBeneficiaryContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [edit, setEdit] = React.useState<boolean>(false);
    const [tempData, setTempData] = React.useState<BeneficiaryServiceRequest | undefined>(
        undefined
    );
    const [currentPercentage, setCurrentPercentage] = React.useState<number>(0);

    function buildTempBeneficiary(request: BeneficiaryServiceRequest): BeneficiaryInfo {
        return {
            kycId: request.kycId ? request.kycId : 0,
            completeName: `${request.name} ${request.lastName} ${request.secondLastName}`,
            participationPercent: request.participationPercent || 0,
            foreignCitizen: request.foreignCitizen || false,
        };
    }

    async function validateData(request: BeneficiaryServiceRequest) {
        setActive(true);
        setTempBeneficiary(buildTempBeneficiary(request));
        const aux: Array<BeneficiaryInfo> = [...beneficiaries];
        const index = beneficiaries.findIndex((item) => item.kycId === request.kycId);
        if (index >= 0) {
            setBeneficiaries([]);
            aux[index].participationPercent = request.participationPercent || 0;
            setBeneficiaries([...aux]);
        } else {
            const tmpBeneficiaries: Array<BeneficiaryInfo> = [...beneficiaries];
            setBeneficiaries([...tmpBeneficiaries, buildTempBeneficiary(request)]);
        }
    }

    async function onSubmit(values: BeneficiaryServiceRequest) {
        try {
            setIsLoading(true);
            const request: BeneficiaryServiceRequest = {
                ...clearUnusedFields(values, fields),
                expedientId: expedientId,
                fatherKycId: fatherKycId,
                viewFatherId: flow.data.fatherId,
                viewId: flow.data.viewId,
                birthDate: formatDateToISO(values.birthDate),
                kycId: kycId,
            };

            const response: EfeResponse<BeneficiaryServiceRequest> =
                await ClientService.saveBeneficiary(request);
            if (response && response.data) {
                setKycId(response.data.kycId ? response.data.kycId : 0);
                setCurrentPercentage(request.participationPercent || 0);
                setPercentageCompleted(
                    percentageCompleted -
                        currentPercentage +
                        Number(request.participationPercent || 0)
                );
                enqueueSnackbar(Messages.BN_INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                const path = nextStep(flow, menu, true);
                if (Constants.MODULE_STEP !== path) {
                    response.data.participationPercent = request.participationPercent;
                    validateData({...request, kycId: response.data.kycId});
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_BN_INFORMATION);
        }
    }

    async function overrideFields(view: View | undefined) {
        if (view && view.fields) {
            setFields(view.fields);
        } else {
            setFields(viewFields);
        }
    }

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
    }, []);

    React.useEffect(() => {
        if (fatherKycId > 0 && kycId > 0 && ViewKeys.BENEFICIARY_INFO === flow.data.expandKey) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<BeneficiaryServiceRequest> =
                        await ClientService.getBeneficiary(kycId);
                    if (response && response.data) {
                        setTempData(response.data);
                        setTempBeneficiary(buildTempBeneficiary(response.data));
                        setCurrentPercentage(response.data.participationPercent || 0);
                    }
                    setEdit(true);
                    setIsLoading(false);
                } catch (error) {
                    setEdit(true);
                    setIsLoading(false);
                }
            })();
        } else if (fatherKycId > 0 && kycId === -1 && !edit) {
            setEdit(true);
        }
    }, [kycId]);

    const accordion: EfeAccordionValues = {
        icon: <ActionIcon />,
        title: 'Informaci\u00F3n personal del Beneficiario',
        reactElement: (
            <Box mt={8}>
                {!isLoading && edit && (
                    <PersonalInfoForm
                        viewId={flow.data.viewId}
                        viewKey={flow.data.id}
                        fields={[
                            ...fieldCatalogGetter(
                                fields.filter((field) => ATTR_NULL !== field.attrName)
                            ),
                        ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                        submitHandler={onSubmit}
                        fieldHandler={overrideFields}
                        tempBeneficiary={tempData}
                        currentPercentage={currentPercentage}
                        accumulatedPercentage={percentageCompleted}
                    />
                )}
            </Box>
        ),
        expandIcon: true,
        disabled: false,
        expanded: ViewKeys.BENEFICIARY_INFO === flow.data.expandKey,
    };

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion
                onChange={() => history.push(HistoryRoutes.BENEFICIARY_INFO)}
                values={[accordion]}
            />
        </MainContent>
    );
}

export default BeneficiaryPersonalInfo;
